import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

// fbeta brand: teal #0B5B73 / maroon #A82A42, 8-bit block iconography,
// square modules with right angles - no rounded corners anywhere.
const TEAL = '#0B5B73';
const TEAL_BRIGHT = '#13849E';
const MAROON = '#A82A42';
const MAROON_BRIGHT = '#C93A55';
const INK = '#0D1214';
const SURFACE = '#141B1E';
const SURFACE_RAISED = '#1B2529';
const BORDER = '#25333770';

export const fbetaTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      type: 'dark',
      primary: { main: TEAL_BRIGHT },
      secondary: { main: MAROON_BRIGHT },
      error: { main: MAROON_BRIGHT },
      warning: { main: '#D6A029' },
      info: { main: TEAL_BRIGHT },
      success: { main: '#3F9E6D' },
      background: {
        default: INK,
        paper: SURFACE,
      },
      banner: {
        info: TEAL,
        error: MAROON,
        text: '#E8EEEF',
        link: TEAL_BRIGHT,
      },
      errorBackground: '#3A171E',
      warningBackground: '#3A2E0F',
      infoBackground: '#0F2A32',
      navigation: {
        background: INK,
        indicator: MAROON_BRIGHT,
        color: '#9FB3B8',
        selectedColor: '#F2F6F6',
        navItem: {
          hoverBackground: '#1B252970',
        },
      },
      text: {
        primary: '#E8EEEF',
        secondary: '#9FB3B8',
      },
      divider: BORDER,
    },
  }),
  defaultPageTheme: 'home',
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  htmlFontSize: 16,
  pageTheme: {
    home: genPageTheme({ colors: [MAROON, TEAL], shape: shapes.round }),
    documentation: genPageTheme({ colors: [TEAL, MAROON], shape: shapes.round }),
    tool: genPageTheme({ colors: [TEAL, TEAL_BRIGHT], shape: shapes.round }),
    service: genPageTheme({ colors: [TEAL, TEAL_BRIGHT], shape: shapes.round }),
    website: genPageTheme({ colors: [TEAL, TEAL_BRIGHT], shape: shapes.round }),
    library: genPageTheme({ colors: [TEAL, TEAL_BRIGHT], shape: shapes.round }),
    other: genPageTheme({ colors: [TEAL, TEAL_BRIGHT], shape: shapes.round }),
    app: genPageTheme({ colors: [TEAL, TEAL_BRIGHT], shape: shapes.round }),
    apis: genPageTheme({ colors: [TEAL, TEAL_BRIGHT], shape: shapes.round }),
    infrastructure: genPageTheme({
      colors: [TEAL, TEAL_BRIGHT],
      shape: shapes.round,
    }),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': [],
        body: {
          backgroundImage:
            'linear-gradient(180deg, rgba(11,91,115,0.06) 0%, rgba(0,0,0,0) 240px)',
        },
      },
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: false },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.02em',
        },
        contained: {
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: `1px solid ${BORDER}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: BORDER,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: MAROON_BRIGHT,
          height: 3,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: INK,
          borderRight: `1px solid ${BORDER}`,
        },
      },
    },
  },
});

export const fbetaThemeColors = {
  TEAL,
  TEAL_BRIGHT,
  MAROON,
  MAROON_BRIGHT,
  INK,
  SURFACE,
  SURFACE_RAISED,
  BORDER,
};
